import { BREAKPOINTS } from "src/theme";
import { getRuntimeConfig } from "src/utils/runtime-config";

export interface IQueryParams {
    w?: number;
    h?: number;
    fit?: "clamp" | "clip" | "crop" | "facearea" | "fill" | "fillmax" | "max" | "min" | "scale";
    dpr?: number;
    auto?: string;
}

export interface ISrc {
    queryParams?: IQueryParams;
    url?: string;
}

export interface IBuildImageSourcesParams {
    baseUrl?: string;
    queryParams?: IQueryParams;
}

export interface IImageSources {
    src: string;
    srcSet: string;
}

interface IMediaSource {
    minWidth: string;
    srcSet: string;
}

interface IPictureSources {
    mediaSources: IMediaSource[];
    defaultImage: IImageSources;
}

const DEFAULT_QUERY_PARAMS: IQueryParams = {
    auto: "format",
};

const DEVICE_PIXEL_RATIOS = [1, 2] as const;
const BREAKPOINT_VALUES = Object.values(BREAKPOINTS);

function splitUrl(url: string): { path: string; query: string } {
    const queryStart = url.indexOf("?");
    if (queryStart === -1) {
        return { path: url, query: "" };
    }

    return { path: url.slice(0, queryStart), query: url.slice(queryStart + 1) };
}

function buildUrl(baseUrl?: string, queryParams?: IQueryParams): string {
    if (!baseUrl) {
        return "";
    }

    const { path, query } = splitUrl(baseUrl);
    const searchParams = new URLSearchParams(query);

    Object.entries({ ...DEFAULT_QUERY_PARAMS, ...queryParams }).forEach(([key, value]) => {
        if (value !== undefined) {
            searchParams.set(key, String(value));
        }
    });

    return `${path}?${searchParams.toString()}`;
}

function canServeDevicePixelRatioCandidates(queryParams?: IQueryParams): boolean {
    const hasLayoutDimensions = Boolean(queryParams?.w || queryParams?.h);
    return hasLayoutDimensions && !queryParams?.dpr;
}

function buildDevicePixelRatioUrl(baseUrl: string, queryParams: IQueryParams | undefined, dpr: number): string {
    return buildUrl(baseUrl, { ...queryParams, dpr });
}

export function buildImageSources({ baseUrl, queryParams }: IBuildImageSourcesParams): IImageSources {
    if (!baseUrl || !canServeDevicePixelRatioCandidates(queryParams)) {
        const url = buildUrl(baseUrl, queryParams);
        return { src: url, srcSet: url };
    }

    const [standardDensity] = DEVICE_PIXEL_RATIOS;
    const srcSet = DEVICE_PIXEL_RATIOS.map(dpr => `${buildDevicePixelRatioUrl(baseUrl, queryParams, dpr)} ${dpr}x`).join(", ");

    return { src: buildDevicePixelRatioUrl(baseUrl, queryParams, standardDensity), srcSet };
}

function buildMediaSources(baseUrl: string, breakpointSources: ISrc[]): IMediaSource[] {
    return breakpointSources
        .flatMap((src, index) => {
            const minWidth = BREAKPOINT_VALUES[index];
            if (!src || !minWidth) {
                return [];
            }

            return [{ minWidth, srcSet: buildImageSources({ baseUrl: src.url || baseUrl, queryParams: src.queryParams }).srcSet }];
        })
        .reverse();
}

export function buildPictureSources(baseUrl: string, srcSet?: ISrc[]): IPictureSources {
    const [defaultSrc, ...breakpointSources] = srcSet ?? [];

    return {
        mediaSources: buildMediaSources(baseUrl, breakpointSources),
        defaultImage: buildImageSources({ baseUrl: defaultSrc?.url || baseUrl, queryParams: defaultSrc?.queryParams }),
    };
}

export function getImageBaseUrl(): string {
    const { imgixUrl, cloudBlobEnvironmentShortName } = getRuntimeConfig();
    return `${imgixUrl.endsWith("/") ? imgixUrl : imgixUrl + "/"}${cloudBlobEnvironmentShortName}/`;
}
